import { useState } from 'react';
import { BrowseResults } from '../../components/BrowseResults';
import { type TagFilter, emptyTagFilter, normalizeTagFilter } from '../../components/TagFilterBuilder';
import { PageHeader } from '../../components/PageHeader';
import { Breadcrumb } from '../../components/Breadcrumb';
import { getRouteParams, getCurrentFilter, setCurrentFilter } from '../../services/angular-bridge';
import { useProject } from '../../hooks/useProject';
import { useCollections } from '../../hooks/useCollections';
import { useProjectPageMount } from '../../hooks/useProjectPageMount';

/**
 * BrowsePage — the Tag Explorer (/project/:id/browse). Browses resources matching a
 * tag filter across all project collections. The initial filter is set before
 * navigation (navigateToTagExplorer, or a prior visit) and read from PreferenceService
 * via the bridge; the grid toolbar's tag filter (M3f) lets the user refine it in place.
 */
export function BrowsePage() {
  const { projectId } = getRouteParams();
  const { data: project } = useProject(projectId);
  const { data: collections } = useCollections(projectId);
  useProjectPageMount(project, 'projects');
  // Read once on mount; the filter is set synchronously before we navigate here. The
  // cookie is shared with Angular and may hold a partial or other-project filter.
  const [filter, setFilter] = useState<TagFilter | null>(() =>
    normalizeTagFilter(getCurrentFilter(), projectId)
  );

  const applyFilter = (next: TagFilter) => {
    setCurrentFilter(next); // persist to the cookie (Angular parity)
    setFilter(next);
  };

  return (
    <div className="container-fluid">
      <PageHeader icon="far fa-hdd" title="Tag Explorer" />
      <Breadcrumb
        items={[
          { label: 'Projects', href: '/project' },
          { label: project?.name ?? 'Project', href: `/project/${projectId}/collections` },
          { label: 'Tag Explorer' },
        ]}
      />
      {collections && collections.length > 0 && (
        <span className="label label-default" title={collections.map((c) => c.name).join('\n')}>
          <i className="far fa-hdd" aria-hidden="true" /> {collections.length} Collections
        </span>
      )}
      <BrowseResults
        projectId={projectId}
        filter={filter ?? emptyTagFilter(projectId)}
        onFilterChange={applyFilter}
      />
    </div>
  );
}
